import React from "react";
import {
	Avatar,
	Box,
	Button,
	ButtonBase,
	Typography,
	colors,
	Menu,
	MenuItem,
	useTheme,
	useMediaQuery,
	IconButton
} from "@mui/material";
import { Link } from "react-router-dom";
import VL_Logo from "../../assets/imgs/vl-logo-transparentBg.svg";

import { HeaderWrapper } from "./styles";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../../store";
import { getFullState, logOut, setRunGuide } from "../../store/slices/auth/auth.slice";
import { googleLogout } from "@react-oauth/google";
import { AccountType } from "../../store/services/auth/dto/auth.dto";
import { api } from "../../store/services";
import HelpIcon from "@mui/icons-material/Help";
import MenuIcon from "@mui/icons-material/Menu";

const NAV_LINKS = [
	{ to: "/invitation-templates", label: "Invitations" },
	{ to: "/features", label: "Features" },
	{ to: "/pricing", label: "Pricing" },
	{ to: "/howitworks", label: "How it works" },
	{ to: "/aboutus", label: "About" },
	{ to: "/contactus", label: "Contact" }
];

const Header = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const theme = useTheme();
	const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const { isLoggedIn, user } = useAppSelector(getFullState);
	const dispatch = useAppDispatch();
	const pathFilterList = ["/login", "/signup", "/resetPassword"];
	const hideLoginControl = pathFilterList.some(p => location.pathname.includes(p));
	const isLandingPage = location.pathname === "/";
	const canShowInvitationGuide =
		location.pathname.includes("/manageWeddingInvitation") && location.pathname.split("/").length === 3;
	const loginButtonColor = theme.palette.common.white;

	const [navAnchor, setNavAnchor] = React.useState<null | HTMLElement>(null);

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleLogOut = () => {
		if (user?.account_type === AccountType.SOCIAL) {
			googleLogout();
		}
		dispatch(logOut());
		dispatch(api.util.resetApiState());
		localStorage.clear();
		navigate("/login");
		handleClose();
	};

	const handleMyAccount = () => {
		navigate("/myAccount");
		handleClose();
	};
	const handleAdmin = () => {
		navigate("/admin");
		handleClose();
	};

	const handleMyInvitations = () => {
		navigate("/myAccount/invitations");
		handleClose();
	};

	return (
		<HeaderWrapper $isLandingPage={isLandingPage}>
			<Box display={"flex"} flexDirection={"row"} alignItems={"center"} justifyContent={"space-between"} px={2} py={1}>
				<Link
					to={"/"}
					style={{
						display: "flex",
						alignItems: "center",
						cursor: "pointer",
						textDecoration: "none"
					}}
				>
					<img src={VL_Logo} alt="VL Logo" width={40} />
					<Box ml={1}>
						<Typography
							variant={"h6"}
							fontWeight={"bold"}
							color={"textPrimary"}
							sx={{ fontFamily: "Roboto", color: colors.common.white }}
						>
							VitesLink
						</Typography>
					</Box>
				</Link>
				{!isSmallerScreen && (
					<Box component={"nav"} aria-label={"Main"} display={"flex"} alignItems={"center"} gap={3.5}>
						{NAV_LINKS.map(l => (
							<Link
								key={l.to}
								to={l.to}
								style={{
									color: loginButtonColor,
									fontSize: "15px",
									fontWeight: location.pathname === l.to ? 700 : 500,
									opacity: location.pathname === l.to ? 1 : 0.88,
									whiteSpace: "nowrap"
								}}
							>
								{l.label}
							</Link>
						))}
					</Box>
				)}
				{!hideLoginControl && (
					<Box display={"flex"} alignItems={"center"} gap={1}>
						{isSmallerScreen && (
							<>
								<IconButton aria-label="Open menu" onClick={e => setNavAnchor(e.currentTarget)}>
									<MenuIcon style={{ fill: "#fff" }} />
								</IconButton>
								<Menu anchorEl={navAnchor} open={!!navAnchor} onClose={() => setNavAnchor(null)}>
									{NAV_LINKS.map(l => (
										<MenuItem
											key={l.to}
											onClick={() => {
												setNavAnchor(null);
												navigate(l.to);
											}}
										>
											{l.label}
										</MenuItem>
									))}
								</Menu>
							</>
						)}
						{isLoggedIn && user ? (
							<Box display={"flex"}>
								{!isSmallerScreen && (
									<Box display={"flex"} alignItems={"center"} gap={2} mx={1}>
										{user.account_type === AccountType.ADMIN && (
											<Link
												to={"/admin"}
												style={{
													color: loginButtonColor,
													marginRight: 1,
													fontWeight: "bold",
													letterSpacing: "1px"
												}}
											>
												Admin
											</Link>
										)}

										<Link
											to={"/myAccount/invitations"}
											style={{
												color: loginButtonColor,
												marginRight: 1,
												fontWeight: "bold",
												letterSpacing: "1px"
											}}
										>
											My Invitations
										</Link>
									</Box>
								)}
								{canShowInvitationGuide && (
									<IconButton
										onClick={() => {
											localStorage.removeItem("guide_skipped");
											dispatch(setRunGuide(true));
										}}
									>
										<HelpIcon style={{ fill: "#fff", fontSize: "24px" }} />
									</IconButton>
								)}
								<ButtonBase onClick={handleClick}>
									<Avatar sx={{ bgcolor: colors.deepOrange[500], fontSize: "16px" }}>
										{user?.firstName[0] + user?.lastName[0]}
									</Avatar>
								</ButtonBase>
								<Menu
									id="basic-menu"
									anchorEl={anchorEl}
									open={!!anchorEl}
									onClose={handleClose}
									MenuListProps={{
										"aria-labelledby": "basic-button"
									}}
								>
									<MenuItem onClick={handleMyAccount}>My account</MenuItem>
									{isSmallerScreen && <MenuItem onClick={handleMyInvitations}> My Invitations</MenuItem>}
									{user.account_type === AccountType.ADMIN && <MenuItem onClick={handleAdmin}>Admin</MenuItem>}
									<MenuItem onClick={handleLogOut}>Logout</MenuItem>
								</Menu>
							</Box>
						) : (
							<Button
								variant={"outlined"}
								sx={{
									color: loginButtonColor,
									borderColor: loginButtonColor
								}}
								onClick={() => navigate("/login")}
							>
								Login
							</Button>
						)}
					</Box>
				)}
			</Box>
		</HeaderWrapper>
	);
};

export default Header;
